import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Shield, 
  ShieldCheck, 
  UserPlus, 
  UserMinus,
  Search,
  Loader2,
  ChevronRight,
  AlertCircle,
  Check,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';

export default function StaffManagementPage() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);
  
  // Promote dialog
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [promoteEmail, setPromoteEmail] = useState('');
  const [promoting, setPromoting] = useState(false);
  
  // Demote dialog
  const [showDemoteDialog, setShowDemoteDialog] = useState(false);
  const [demoteUser, setDemoteUser] = useState(null);
  const [demoting, setDemoting] = useState(false);
  
  // Role change dialog
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [changingRole, setChangingRole] = useState(false);

  useEffect(() => {
    fetchStaff();
    fetchPermissions();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/staff');
      setStaff(response.data.staff || []);
    } catch (err) {
      console.error('Failed to fetch staff:', err);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to view this page');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load staff members');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await api.get('/admin/permissions');
      setPermissions(response.data.roles);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  };

  const handlePromote = async () => {
    if (!promoteEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      setPromoting(true);
      await api.post(`/admin/staff/promote?user_email=${encodeURIComponent(promoteEmail)}`);
      toast.success(`${promoteEmail} has been promoted to staff`);
      setShowPromoteDialog(false);
      setPromoteEmail('');
      fetchStaff();
    } catch (err) {
      console.error('Promote error:', err);
      toast.error(err.response?.data?.detail || 'Failed to promote user');
    } finally {
      setPromoting(false);
    }
  };

  const handleDemote = async () => {
    if (!demoteUser) return;
    
    try {
      setDemoting(true);
      await api.post(`/admin/staff/demote/${demoteUser.id}`);
      toast.success(`${demoteUser.email} has been demoted to regular user`);
      setShowDemoteDialog(false);
      setDemoteUser(null);
      fetchStaff();
    } catch (err) {
      console.error('Demote error:', err);
      toast.error(err.response?.data?.detail || 'Failed to demote user');
    } finally {
      setDemoting(false);
    }
  };

  const handleRoleChange = async () => {
    if (!roleChangeUser || !newRole) return;
    
    try {
      setChangingRole(true);
      await api.put(`/admin/users/${roleChangeUser.id}/role?role=${newRole}`);
      toast.success(`${roleChangeUser.email}'s role changed to ${newRole}`);
      setShowRoleDialog(false);
      setRoleChangeUser(null);
      setNewRole('');
      fetchStaff();
    } catch (err) {
      console.error('Role change error:', err);
      toast.error(err.response?.data?.detail || 'Failed to change role');
    } finally {
      setChangingRole(false);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return (
          <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
            <ShieldCheck className="w-3 h-3 mr-1" />
            Admin
          </Badge>
        );
      case 'staff':
        return (
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">
            <Shield className="w-3 h-3 mr-1" />
            Staff
          </Badge>
        );
      case 'account_manager':
        return (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">
            <Users className="w-3 h-3 mr-1" />
            Account Manager
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-gray-400">
            User
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const adminCount = staff.filter(s => s.role === 'admin').length;
  const staffCount = staff.filter(s => s.role === 'staff').length;
  const amCount = staff.filter(s => s.role === 'account_manager').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Staff Management</h1>
          <p className="text-gray-400">Manage team members and their access levels</p>
        </div>
        <Button
          onClick={() => setShowPromoteDialog(true)}
          className="bg-gradient-to-r from-blue-500 to-purple-500"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Add Staff Member
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Team</p>
                <p className="text-2xl font-bold text-white">{staff.length}</p>
              </div>
              <Users className="w-8 h-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Admins</p>
                <p className="text-2xl font-bold text-red-400">{adminCount}</p>
              </div>
              <ShieldCheck className="w-8 h-8 text-red-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Staff</p>
                <p className="text-2xl font-bold text-blue-400">{staffCount}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-500/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gray-800/50 border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Account Managers</p>
                <p className="text-2xl font-bold text-purple-400">{amCount}</p>
              </div>
              <Users className="w-8 h-8 text-purple-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Staff Table */}
      <Card className="bg-gray-800/50 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Team Members</CardTitle>
          <CardDescription>Manage roles and access for your team</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-gray-700">
                <TableHead className="text-gray-400">Name</TableHead>
                <TableHead className="text-gray-400">Email</TableHead>
                <TableHead className="text-gray-400">Role</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staff.map((member) => (
                <TableRow key={member.id} className="border-gray-700">
                  <TableCell className="text-white font-medium">
                    {member.name || 'No name'}
                  </TableCell>
                  <TableCell className="text-gray-300">{member.email}</TableCell>
                  <TableCell>{getRoleBadge(member.role)}</TableCell>
                  <TableCell>
                    {member.is_active ? (
                      <Badge variant="outline" className="text-green-400 border-green-400/30">
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-400 border-red-400/30">
                        Inactive
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setRoleChangeUser(member);
                          setNewRole(member.role);
                          setShowRoleDialog(true);
                        }}
                        className="text-gray-400 hover:text-white"
                      >
                        Change Role
                      </Button>
                      {member.role === 'staff' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDemoteUser(member);
                            setShowDemoteDialog(true);
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <UserMinus className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role Permissions Info */}
      {permissions && (
        <Card className="bg-gray-800/50 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white">Role Permissions</CardTitle>
            <CardDescription>What each role can do</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-5 h-5 text-blue-400" />
                  <h3 className="font-semibold text-blue-400">Staff</h3>
                </div>
                <p className="text-sm text-gray-400 mb-3">{permissions.staff?.description}</p>
                <div className="space-y-1">
                  {permissions.staff?.permissions?.slice(0, 8).map((perm) => (
                    <div key={perm} className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-3 h-3 text-green-400" />
                      {perm.replace(/_/g, ' ')}
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-5 h-5 text-red-400" />
                  <h3 className="font-semibold text-red-400">Admin</h3>
                </div>
                <p className="text-sm text-gray-400 mb-3">{permissions.admin?.description}</p>
                <div className="space-y-1">
                  <div className="text-sm text-gray-300 flex items-center gap-2">
                    <Check className="w-3 h-3 text-green-400" />
                    All Staff permissions, plus:
                  </div>
                  {['delete_users', 'change_pricing', 'manage_roles', 'system_config'].map((perm) => (
                    <div key={perm} className="flex items-center gap-2 text-sm text-gray-300 ml-4">
                      <Check className="w-3 h-3 text-green-400" />
                      {perm.replace(/_/g, ' ')}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Promote Dialog */}
      <Dialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter the email of the user you want to promote to staff
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="user@example.com"
              value={promoteEmail}
              onChange={(e) => setPromoteEmail(e.target.value)}
              className="bg-gray-800 border-gray-600"
            />
            <p className="mt-2 text-sm text-gray-500">
              The user must already have an account
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowPromoteDialog(false)}
              className="border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handlePromote}
              disabled={promoting}
              className="bg-blue-500 hover:bg-blue-600"
            >
              {promoting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Promoting...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Promote to Staff
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Demote Dialog */}
      <Dialog open={showDemoteDialog} onOpenChange={setShowDemoteDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Demote Staff Member</DialogTitle>
            <DialogDescription className="text-gray-400">
              Are you sure you want to demote {demoteUser?.email} to a regular user?
              They will lose all staff permissions.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDemoteDialog(false)}
              className="border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDemote}
              disabled={demoting}
              variant="destructive"
            >
              {demoting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Demoting...
                </>
              ) : (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Demote to User
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Change Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Change Role</DialogTitle>
            <DialogDescription className="text-gray-400">
              Change role for {roleChangeUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger className="bg-gray-800 border-gray-600">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                <SelectItem value="user">User - Standard access</SelectItem>
                <SelectItem value="account_manager">Account Manager - Creates content for clients</SelectItem>
                <SelectItem value="staff">Staff - Manages users & credits</SelectItem>
                <SelectItem value="admin">Admin - Full access</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRoleDialog(false)}
              className="border-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRoleChange}
              disabled={changingRole || newRole === roleChangeUser?.role}
            >
              {changingRole ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Role'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
